import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import type { RenewalChannel, RenewalChannelOption } from '../renewalChannel';

type Props = {
  options: RenewalChannelOption[];
  value: RenewalChannel;
  onChange: (channel: RenewalChannel) => void;
  /** Id do título que rotula o grupo (`aria-labelledby`). */
  labelledBy: string;
  disabled?: boolean;
  appId: string;
};

const CHANNEL_META: Record<RenewalChannel, { label: string; icon: ReactNode }> = {
  email: { label: 'E-mail', icon: <MailOutlineIcon /> },
  sms: { label: 'SMS', icon: <CommentOutlinedIcon /> }
};

/**
 * Cards de rádio do canal do código (Figma 2079:6265, nodes 2086:6570 e 2086:6563).
 * É um `RadioGroup` de verdade: setas do teclado trocam a opção e o leitor de tela anuncia o grupo.
 */
export default function RenewalChannelSelector({ options, value, onChange, labelledBy, disabled, appId }: Props) {
  return (
    <RadioGroup
      aria-labelledby={labelledBy}
      data-app-id={appId}
      onChange={(event) => onChange(event.target.value as RenewalChannel)}
      sx={{ display: 'grid', gap: '8px', gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' }, width: '100%' }}
      value={value}
    >
      {options.map((option) => {
        const selected = option.channel === value;
        const meta = CHANNEL_META[option.channel];
        return (
          <FormControlLabel
            control={<Radio inputProps={{ 'aria-label': `${meta.label} ${option.contact}`.trim() }} />}
            data-app-id={`${appId}-${option.channel}`}
            disabled={disabled || option.disabled}
            key={option.channel}
            label={
              <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px', minWidth: 0 }}>
                <Box
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: '100px',
                    color: 'primary.light',
                    display: 'flex',
                    flexShrink: 0,
                    height: 40,
                    justifyContent: 'center',
                    width: 40
                  }}
                >
                  {meta.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ color: 'text.primary', fontSize: 12, fontWeight: 500, lineHeight: '16px' }}
                    variant="overline"
                  >
                    {meta.label}
                  </Typography>
                  <Typography noWrap sx={{ color: 'text.secondary' }} variant="body1">
                    {option.contact || 'Não cadastrado'}
                  </Typography>
                </Box>
              </Box>
            }
            labelPlacement="start"
            sx={(theme) => ({
              bgcolor: selected ? theme.customColors.backgroundTertiary : 'background.paper',
              border: '1px solid',
              borderColor: selected ? 'primary.light' : 'divider',
              borderRadius: '8px',
              justifyContent: 'space-between',
              m: 0,
              minWidth: 0,
              p: '8px',
              '& .MuiFormControlLabel-label': { minWidth: 0 },
              '&.Mui-disabled': { opacity: 0.6 }
            })}
            value={option.channel}
          />
        );
      })}
    </RadioGroup>
  );
}
